import { useState, useEffect, useRef, useCallback } from "react";
import { X, Send, Paperclip, Save, Bold, Italic, List, Link as LinkIcon } from "lucide-react";
import EmailChipInput from "./EmailChipInput";
import cloudflareApi from "../lib/cloudflare-api";

const MAX_ATTACHMENT_SIZE = 25 * 1024 * 1024;

export interface ComposeData {
  to?: string;
  subject?: string;
  body?: string;
  mode?: 'reply' | 'replyAll' | 'forward';
  originalSender?: string;
  originalTo?: string;
  draftId?: string;
  cc?: string;
  bcc?: string;
  attachments?: string[];
}

interface ComposeModalProps {
  onClose: () => void;
  initialData?: ComposeData;
}

interface FormatState {
  bold: boolean;
  italic: boolean;
  list: boolean;
}

export default function ComposeModal({ onClose, initialData }: ComposeModalProps) {
  const [to, setTo] = useState<string[]>([]);
  const [cc, setCc] = useState<string[]>([]);
  const [bcc, setBcc] = useState<string[]>([]);
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [subject, setSubject] = useState("");
  const [sending, setSending] = useState(false);
  const [attachments, setAttachments] = useState<string[]>([]);
  const [attachmentFiles, setAttachmentFiles] = useState<File[]>([]);
  const [draftId, setDraftId] = useState<string | undefined>(undefined);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [formatState, setFormatState] = useState<FormatState>({ bold: false, italic: false, list: false });
  const autoSaveTimerRef = useRef<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const editorRef = useRef<HTMLDivElement>(null);

  const getEditorContent = useCallback(() => {
    return editorRef.current?.innerHTML || '';
  }, []);

  const getEditorTextContent = useCallback(() => {
    return editorRef.current?.innerText || '';
  }, []);

  useEffect(() => {
    if (initialData) {
      if (initialData.to) setTo(initialData.to.split(',').map(e => e.trim()).filter(e => e));
      if (initialData.subject) setSubject(initialData.subject);
      if (initialData.body && editorRef.current) {
        if (initialData.body.includes('<') && initialData.body.includes('>')) {
          editorRef.current.innerHTML = initialData.body;
        } else {
          editorRef.current.innerText = initialData.body;
        }
      }
      if (initialData.draftId) setDraftId(initialData.draftId);
      if (initialData.cc) {
        setCc(initialData.cc.split(',').map(e => e.trim()).filter(e => e));
        setShowCc(true);
      }
      if (initialData.bcc) {
        setBcc(initialData.bcc.split(',').map(e => e.trim()).filter(e => e));
        setShowBcc(true);
      }
      if (initialData.attachments) {
        setAttachments(initialData.attachments);
      }
    }
  }, [initialData]);

  useEffect(() => {
    if (editorRef.current && initialData?.body) {
      if (initialData.body.includes('<') && initialData.body.includes('>')) {
        editorRef.current.innerHTML = initialData.body;
      } else {
        editorRef.current.innerText = initialData.body;
      }
    }
  }, []);

  useEffect(() => {
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    const editorContent = getEditorContent();
    if (to.length > 0 || subject || editorContent) {
      autoSaveTimerRef.current = setTimeout(() => {
        handleSaveDraft(true);
      }, 30000);
    }

    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [to, cc, bcc, subject, attachments]);

  const updateFormatState = useCallback(() => {
    const bold = document.queryCommandState('bold');
    const italic = document.queryCommandState('italic');
    const list = document.queryCommandState('insertUnorderedList');
    setFormatState({ bold, italic, list });
  }, []);

  const handleSaveDraft = async (isAutoSave = false) => {
    const htmlContent = getEditorContent();
    const textContent = getEditorTextContent();

    if (to.length === 0 && !subject && !textContent) return;

    try {
      setIsSavingDraft(true);

      const currentDraftId = draftId || `draft_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const hasHtmlFormatting = htmlContent !== textContent && htmlContent.includes('<');

      await cloudflareApi.syncEmail({
        id: currentDraftId,
        recipient: to.join(', '),
        subject,
        body_plain: textContent,
        body_html: hasHtmlFormatting ? htmlContent : '',
        folder: 'drafts',
        is_draft: 1,
        is_read: 1,
        to_field: to.join(', '),
        cc_field: cc.length > 0 ? cc.join(', ') : null,
        bcc_field: bcc.length > 0 ? bcc.join(', ') : null,
        timestamp: Date.now(),
        sender: '',
      });

      setDraftId(currentDraftId);
      setLastSaved(new Date());

      if (!isAutoSave) {
        alert("✅ Borrador guardado");
      }
    } catch (error) {
      console.error("Error guardando borrador:", error);
      if (!isAutoSave) {
        alert("Error al guardar borrador");
      }
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handleSelectFiles = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const currentSize = attachmentFiles.reduce((sum, f) => sum + f.size, 0);
    const newSize = files.reduce((sum, f) => sum + f.size, 0);

    if (currentSize + newSize > MAX_ATTACHMENT_SIZE) {
      alert(`El tamaño total de adjuntos no puede superar ${MAX_ATTACHMENT_SIZE / 1024 / 1024}MB`);
      return;
    }

    setAttachmentFiles([...attachmentFiles, ...files]);
    setAttachments([...attachments, ...files.map(f => f.name)]);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const applyFormat = (format: string) => {
    editorRef.current?.focus();

    switch (format) {
      case 'bold':
        document.execCommand('bold', false);
        break;
      case 'italic':
        document.execCommand('italic', false);
        break;
      case 'list':
        document.execCommand('insertUnorderedList', false);
        break;
      case 'link':
        const selection = window.getSelection();
        const selectedText = selection?.toString() || '';
        const url = prompt('Ingresa la URL del enlace:', 'https://');
        if (url) {
          if (selectedText) {
            document.execCommand('createLink', false, url);
          } else {
            document.execCommand('insertHTML', false, `<a href="${url}">${url}</a>`);
          }
        }
        break;
      default:
        return;
    }

    updateFormatState();
  };

  const handleRemoveAttachment = (index: number) => {
    setAttachments(attachments.filter((_, i) => i !== index));
    setAttachmentFiles(attachmentFiles.filter((_, i) => i !== index));
  };

  const getFileName = (path: string) => {
    return path.split('/').pop() || path;
  };

  const handleSend = async (e?: React.MouseEvent) => {
    e?.preventDefault();

    const htmlContent = getEditorContent();
    const textContent = getEditorTextContent();
    const hasHtmlFormatting = htmlContent !== textContent && htmlContent.includes('<');

    console.log("=== INICIANDO ENVÍO DE CORREO ===");
    console.log("Para:", to);
    console.log("CC:", cc);
    console.log("BCC:", bcc);
    console.log("Asunto:", subject);
    console.log("Adjuntos:", attachmentFiles.length);
    console.log("HTML:", hasHtmlFormatting);

    if (to.length === 0 || !subject) {
      console.warn("Faltan campos requeridos");
      alert("Por favor completa destinatario y asunto");
      return;
    }

    try {
      setSending(true);

      const tempEmailId = `email_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const uploadedAttachments: Array<{ id: string; filename: string }> = [];

      if (attachmentFiles.length > 0) {
        console.log("Subiendo adjuntos a R2...");
        for (const file of attachmentFiles) {
          try {
            const result = await cloudflareApi.uploadAttachment(file, tempEmailId);
            uploadedAttachments.push({
              id: result.attachment_id,
              filename: result.filename
            });
            console.log(`✅ Adjunto subido: ${file.name}`);
          } catch (err) {
            console.error(`Error subiendo ${file.name}:`, err);
            throw new Error(`Error subiendo adjunto ${file.name}: ${err}`);
          }
        }
      }

      console.log("Llamando a cloudflareApi.sendEmail()...");

      const result = await cloudflareApi.sendEmail({
        to: to.join(', '),
        subject,
        body: hasHtmlFormatting ? htmlContent : textContent,
        cc: cc.length > 0 ? cc.join(', ') : undefined,
        bcc: bcc.length > 0 ? bcc.join(', ') : undefined,
        is_html: hasHtmlFormatting,
        attachments: uploadedAttachments.length > 0 ? uploadedAttachments : undefined,
      });

      console.log("Resultado:", result);
      console.log("✅ Correo enviado exitosamente");

      if (draftId) {
        try {
          await cloudflareApi.deleteEmail(draftId);
        } catch (e) {
          console.error("Error eliminando borrador:", e);
        }
      }

      alert("✅ Correo enviado exitosamente");
      onClose();
    } catch (error) {
      console.error("❌ Error al enviar correo:", error);
      const errorMessage = typeof error === 'string' ? error : String(error);
      alert(`❌ Error: ${errorMessage}`);
    } finally {
      setSending(false);
      console.log("=== FIN DEL PROCESO DE ENVÍO ===");
    }
  };

  const getTitle = () => {
    if (initialData?.mode === 'reply') return 'Responder';
    if (initialData?.mode === 'replyAll') return 'Responder a todos';
    if (initialData?.mode === 'forward') return 'Reenviar';
    return 'Nuevo mensaje';
  };

  const controlBtnStyle: React.CSSProperties = {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '4px',
    borderRadius: '4px',
    color: 'var(--text-tertiary)',
    display: 'flex',
    alignItems: 'center',
    transition: 'color 150ms ease, background-color 150ms ease',
  };

  const toolbarBtnStyle = (active: boolean): React.CSSProperties => ({
    background: active ? 'rgba(132, 204, 22, 0.1)' : 'none',
    color: active ? '#84cc16' : 'var(--text-tertiary)',
    border: 'none',
    cursor: 'pointer',
    padding: '6px',
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    transition: 'color 150ms ease, background-color 150ms ease',
  });

  // Full-page inline style (fills main content area)
  const containerStyle: React.CSSProperties = {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    background: 'var(--bg-content)',
    overflow: 'hidden',
    height: '100vh',
  };

  return (
      <div style={containerStyle}>
        {/* Hidden file input */}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          multiple
          style={{ display: 'none' }}
          accept="*/*"
        />

        {/* Header bar */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 16px',
            background: 'var(--bg-sidebar)',
            borderBottom: '1px solid var(--border)',
            flexShrink: 0,
          }}
        >
          <span style={{
            fontWeight: 600,
            fontSize: '15px',
            color: 'var(--text-primary)',
          }}>
            {getTitle()}
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            {lastSaved && (
              <span style={{
                fontSize: '11px',
                color: isSavingDraft ? 'var(--text-tertiary)' : 'var(--primary)',
                marginRight: '4px',
              }}>
                {isSavingDraft ? "Guardando..." : "Guardado"}
              </span>
            )}
            <button onClick={() => handleSaveDraft(false)} style={controlBtnStyle} title="Guardar borrador" disabled={sending || isSavingDraft}>
              <Save size={16} />
            </button>
            <button onClick={() => onClose()} style={controlBtnStyle} title="Cerrar" disabled={sending}>
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Form body */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Fields */}
            <div style={{ padding: '12px 20px 0', flexShrink: 0 }}>
              {/* To */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                <label style={{ fontSize: '12px', color: 'var(--text-tertiary)', fontWeight: 500, minWidth: '36px' }}>Para:</label>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <EmailChipInput value={to} onChange={setTo} placeholder="destinatario@ejemplo.com" />
                </div>
                <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                  {!showCc && (
                    <button
                      type="button"
                      onClick={() => setShowCc(true)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', color: 'var(--text-tertiary)', fontWeight: 500, padding: '2px 4px', transition: 'color 150ms ease' }}
                    >
                      CC
                    </button>
                  )}
                  {!showBcc && (
                    <button
                      type="button"
                      onClick={() => setShowBcc(true)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', color: 'var(--text-tertiary)', fontWeight: 500, padding: '2px 4px', transition: 'color 150ms ease' }}
                    >
                      BCC
                    </button>
                  )}
                </div>
              </div>

              {/* CC */}
              {showCc && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                  <label style={{ fontSize: '12px', color: 'var(--text-tertiary)', fontWeight: 500, minWidth: '36px' }}>CC:</label>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <EmailChipInput value={cc} onChange={setCc} placeholder="copia@ejemplo.com" />
                  </div>
                </div>
              )}

              {/* BCC */}
              {showBcc && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                  <label style={{ fontSize: '12px', color: 'var(--text-tertiary)', fontWeight: 500, minWidth: '36px' }}>BCC:</label>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <EmailChipInput value={bcc} onChange={setBcc} placeholder="copia-oculta@ejemplo.com" />
                  </div>
                </div>
              )}

              {/* Subject */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', paddingBottom: '8px', borderBottom: '1px solid var(--border)' }}>
                <label style={{ fontSize: '12px', color: 'var(--text-tertiary)', fontWeight: 500, minWidth: '36px' }}>Asunto:</label>
                <input
                  type="text"
                  placeholder="Asunto del correo"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  style={{
                    flex: 1,
                    background: 'none',
                    border: 'none',
                    outline: 'none',
                    fontSize: '14px',
                    color: 'var(--text-primary)',
                    padding: '4px 0',
                  }}
                />
              </div>
            </div>

            {/* Editor */}
            <div style={{ flex: 1, overflow: 'auto', padding: '0 20px' }}>
              <div
                ref={editorRef}
                className="compose-editor-rich"
                contentEditable
                onSelect={updateFormatState}
                onKeyUp={updateFormatState}
                onMouseUp={updateFormatState}
                onInput={updateFormatState}
                data-placeholder="Escribe tu mensaje..."
                spellCheck={true}
                lang="es"
                style={{
                  minHeight: '120px',
                  padding: '12px 0',
                  outline: 'none',
                  fontSize: '14px',
                  lineHeight: 1.6,
                  color: 'var(--text-primary)',
                }}
              />
            </div>

            {/* Attachment chips strip */}
            {attachments.length > 0 && (
              <div style={{
                padding: '6px 12px',
                borderTop: '1px solid var(--border)',
                display: 'flex',
                gap: '6px',
                overflowX: 'auto',
                flexShrink: 0,
              }}>
                {attachments.map((file, index) => (
                  <span
                    key={file + index}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      padding: '3px 8px',
                      background: 'var(--bg-hover)',
                      border: '1px solid var(--border)',
                      borderRadius: '6px',
                      fontSize: '12px',
                      color: 'var(--text-secondary)',
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                    }}
                  >
                    <Paperclip size={12} style={{ color: 'var(--primary)' }} />
                    {getFileName(file)}
                    <button
                      onClick={() => handleRemoveAttachment(index)}
                      type="button"
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '0 2px',
                        color: 'var(--text-tertiary)',
                        display: 'flex',
                        transition: 'color 150ms ease',
                      }}
                    >
                      <X size={12} />
                    </button>
                  </span>
                ))}
              </div>
            )}

            {/* Bottom toolbar + send */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '8px 12px',
              borderTop: '1px solid var(--border)',
              flexShrink: 0,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                <button onClick={() => applyFormat('bold')} type="button" title="Negrita" style={toolbarBtnStyle(formatState.bold)}>
                  <Bold size={16} />
                </button>
                <button onClick={() => applyFormat('italic')} type="button" title="Cursiva" style={toolbarBtnStyle(formatState.italic)}>
                  <Italic size={16} />
                </button>
                <button onClick={() => applyFormat('list')} type="button" title="Lista" style={toolbarBtnStyle(formatState.list)}>
                  <List size={16} />
                </button>
                <button onClick={() => applyFormat('link')} type="button" title="Enlace" style={toolbarBtnStyle(false)}>
                  <LinkIcon size={16} />
                </button>
                <div style={{ width: '1px', height: '16px', background: 'var(--border)', margin: '0 4px' }} />
                <button onClick={handleSelectFiles} disabled={sending} type="button" title="Adjuntar" style={toolbarBtnStyle(false)}>
                  <Paperclip size={16} />
                </button>
              </div>

              <button
                onClick={handleSend}
                disabled={sending}
                className="inline-flex items-center justify-center gap-2 font-medium rounded-xl px-5 py-2 text-sm gradient-brand text-white shadow-brand hover:shadow-brand-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-lime-500/50 disabled:opacity-50 disabled:cursor-not-allowed transition-shadow duration-200"
              >
                {sending ? (
                  <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                ) : (
                  <Send size={14} />
                )}
                {sending ? "Enviando..." : "Enviar"}
              </button>
            </div>
          </div>
      </div>
  );
}
